import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@velocity/ui';
import moment from 'moment';
import React, { useEffect } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import countries from '../../../../localization/countries.json';
import { EDevice } from '../../../../redux/sequence/enums';
import store from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import {
  Partner,
  Signer,
  SingersType,
} from '../../../../types/instant-lease-api';
import { getCountryByCode, getText } from '../../../../utils/getter';
import { mapAddressField } from '../../../../utils/helper';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import AdacMembership from '../../../common/adac-membership/adac-membership';
import {
  FormDateField,
  FormRadioCardField,
  FormRow,
  FormSection,
  FormSelectField,
  FormSelectOption,
  FormTextField,
} from '../../../common/velocity-form';

export interface PersonalData {
  uuid?: string;
  first_name: string;
  last_name: string;
  gender: string;
  date_of_birth: string;
  birth_place?: string;
  citizenship?: string;
  street_line: string;
  house_number: string;
  additional_lines?: string;
  postal_code: string;
  locality: string;
  country_code: string;
  oc_number?: string;
  type?: SingersType;
  membership_number?: string;
}

interface PersonalDataFormValues {
  first_name: string;
  last_name: string;
  gender: string;
  date_of_birth: string;
  birth_place?: string;
  citizenship?: string;
  street_line: string;
  house_number: string;
  additional_lines?: string;
  postal_code: string;
  locality: string;
  country_code: string;
  oc_number?: string;
  type?: boolean | null;
  membership_number?: string;
}

interface PersonalDataProps {
  hasSpecialField?: boolean;
  specialFieldTooltip?: string;
  fieldName?: string;
  ocNumber?: string | undefined;
  onSubmit: (owner: PersonalData) => void;
  tabButton: boolean;
  privateCustomer: Signer | undefined;
  loading?: boolean;
  singer?: boolean;
  defaultFirstName?: string;
  defaultLastName?: string;
  /** Registered e-mail shown read-only on top of the form (main applicant) */
  email?: string;
}

// Anrede (Figma: "formkit/select" under "Anrede")
const genderOptions: FormSelectOption[] = [
  { value: 'GENDER_MALE', label: 'Herr' },
  { value: 'GENDER_FEMALE', label: 'Frau' },
];

// Lebensgemeinschaft (co-applicant only)
const partnershipOptions = [
  { value: 'true', label: 'Ja' },
  { value: 'false', label: 'Nein' },
];

const countryOptions: FormSelectOption[] = countries;

const PersonalTemplate: React.FC<PersonalDataProps> = ({
  hasSpecialField,
  specialFieldTooltip,
  fieldName,
  ocNumber,
  onSubmit,
  tabButton,
  privateCustomer,
  loading,
  singer = false,
  defaultFirstName,
  defaultLastName,
  email,
}) => {
  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );
  // sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  const partner = activeApplication?.partner;

  // check if leaseapplication has loyaltyType smart_ADAC
  const isSmartAdac =
    store.getState().leaseApp.activeApplication?.loyalty_card
      ?.loyalty_card_type === 'smart_ADAC';

  // check current tenant is smart
  const isSmartTenant =
    store.getState().leaseApp.carData?.tenant.toLowerCase() === 'smart';

  // Registered e-mail of the applicant (read-only, see Figma "EMail")
  const registeredEmail =
    privateCustomer?.person?.contact_details?.emails?.[0]?.value ?? email;

  // validationSchema
  const validationSchema = yup.object().shape({
    first_name: yup.string().trim().required('Pflichtfeld'),
    last_name: yup.string().trim().required('Pflichtfeld'),
    date_of_birth: yup
      .string()
      .required('Pflichtfeld')
      .matches(
        /^(((0?[1-9]|[12]\d|3[01])\.(0[13578]|[13578]|1[02])\.((1[6-9]|[2-9]\d)\d{2}))|((0?[1-9]|[12]\d|30)\.(0[13456789]|[13456789]|1[012])\.((1[6-9]|[2-9]\d)\d{2}))|((0?[1-9]|1\d|2[0-8])\.0?2\.((1[6-9]|[2-9]\d)\d{2}))|(29\.0?2\.((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/,
        'Bitte Geburtsdatum eintragen (TT.MM.JJJJ)',
      )
      .test({
        name: 'test-age',
        message: getText('18_years_validation_message'),
        test: (age) => moment().year() - moment(age, 'DD.MM.YYYY').year() >= 18,
      }),
    birth_place:
      partner === Partner.KIA_FLEX
        ? yup.string().optional()
        : yup.string().trim().required('Pflichtfeld'),
    citizenship:
      partner === Partner.KIA_FLEX
        ? yup.string().optional()
        : yup.string().required('Pflichtfeld'),
    gender: yup.string().nullable().required('Bitte wähle eine Anrede aus'),
    street_line: yup.string().trim().required('Pflichtfeld'),
    house_number: yup.string().trim().required('Pflichtfeld'),
    postal_code: yup.string().trim().required('Pflichtfeld'),
    locality: yup.string().trim().required('Pflichtfeld'),
    country_code: yup.string().required('Pflichtfeld'),
    oc_number: hasSpecialField
      ? yup.string().required('Pflichtfeld')
      : yup.string().optional(),
    type: singer
      ? yup.boolean().nullable().required('Pflichtfeld')
      : yup.boolean().nullable().optional(),
    membership_number:
      isSmartTenant && isSmartAdac
        ? yup
            .string()
            .required('Bitte gib deine 9-stellige ADAC-Mitgliedsnummer ein.')
            .matches(/^\d{9}$/, 'Die Mitgliedsnummer muss 9-stellig sein')
        : yup.string().optional(),
  });

  // Form hook
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    trigger,
    setValue,
    getValues,
  } = useForm<PersonalDataFormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema) as Resolver<PersonalDataFormValues>,
    defaultValues: {
      first_name: privateCustomer?.person?.first_name ?? defaultFirstName,
      last_name: privateCustomer?.person?.last_name ?? defaultLastName,
      date_of_birth: privateCustomer?.person?.date_of_birth,
      birth_place: privateCustomer?.person?.birth_place,
      gender: privateCustomer?.person?.gender,
      citizenship: privateCustomer?.person?.citizenship,
      street_line: mapAddressField(
        privateCustomer?.person?.contact_details,
        'street_line',
      ),
      house_number: mapAddressField(
        privateCustomer?.person?.contact_details,
        'house_number',
      ),
      additional_lines: mapAddressField(
        privateCustomer?.person?.contact_details,
        'additional_lines',
      ),
      postal_code: mapAddressField(
        privateCustomer?.person?.contact_details,
        'postal_code',
      ),
      locality: mapAddressField(
        privateCustomer?.person?.contact_details,
        'locality',
      ),
      country_code:
        (privateCustomer?.person?.contact_details?.addresses &&
          privateCustomer?.person?.contact_details?.addresses.length &&
          getCountryByCode(
            privateCustomer?.person?.contact_details?.addresses[0].country_code,
          )?.value) ||
        'DEU',
      oc_number: ocNumber,
      type: privateCustomer?.type === SingersType.CO_APPLICANT_IN_PARTNERSHIP,
      membership_number: '',
    },
  });

  useEffect(() => {
    if (privateCustomer?.type !== undefined) {
      setValue(
        'type',
        privateCustomer?.type === SingersType.CO_APPLICANT_IN_PARTNERSHIP,
      );
    }
  }, [privateCustomer?.type, setValue]);

  // Submit Data
  const submitData = ({
    first_name,
    last_name,
    gender,
    date_of_birth,
    birth_place,
    citizenship,
    street_line,
    additional_lines,
    postal_code,
    locality,
    country_code,
    house_number,
    oc_number,
    type,
    membership_number,
  }: PersonalDataFormValues) => {
    onSubmit({
      uuid: privateCustomer?.uuid || uuidv4(),
      first_name,
      last_name,
      date_of_birth,
      birth_place,
      citizenship,
      gender,
      street_line,
      additional_lines,
      postal_code,
      locality,
      country_code,
      house_number,
      oc_number,
      membership_number,
      type: type
        ? SingersType.CO_APPLICANT_IN_PARTNERSHIP
        : SingersType.CO_APPLICANT,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(submitData)}
      noValidate
      data-testid='personal-template-form'
    >
      <div className='flex w-full flex-col gap-6'>
        {isSmartTenant && isSmartAdac && (
          <AdacMembership
            register={register}
            errors={errors}
            setValue={setValue}
            trigger={trigger}
          />
        )}

        <FormSection title='Persönliche Daten'>
          {!singer && (
            <FormTextField
              name='email'
              label='EMail'
              required
              type='email'
              value={registeredEmail ?? ''}
              placeholder='Deine E-Mail-Adresse'
              disabled
              autoComplete='email'
              onChange={() => undefined}
            />
          )}

          <Controller
            name='gender'
            control={control}
            render={({ field, fieldState }) => (
              <FormSelectField
                label='Anrede'
                required
                options={genderOptions}
                {...field}
                errorMessage={fieldState.error?.message}
              />
            )}
          />

          <FormTextField
            label='Vorname(n)'
            required
            autoComplete='given-name'
            defaultValue={getValues('first_name')}
            {...register('first_name')}
            errorMessage={errors.first_name?.message}
          />

          <FormTextField
            label='Nachname(n)'
            required
            autoComplete='family-name'
            defaultValue={getValues('last_name')}
            {...register('last_name')}
            errorMessage={errors.last_name?.message}
          />

          <Controller
            name='date_of_birth'
            control={control}
            render={({ field, fieldState }) => (
              <FormDateField
                label='Geburtsdatum'
                required
                {...field}
                errorMessage={fieldState.error?.message}
              />
            )}
          />

          {partner !== Partner.KIA_FLEX && (
            <>
              <FormTextField
                label='Geburtsort'
                required
                defaultValue={getValues('birth_place')}
                {...register('birth_place')}
                errorMessage={errors.birth_place?.message}
              />

              <Controller
                name='citizenship'
                control={control}
                render={({ field, fieldState }) => (
                  <FormSelectField
                    label='Nationalität'
                    required
                    options={countryOptions}
                    searchable
                    {...field}
                    errorMessage={fieldState.error?.message}
                    dataTestId='citizenship-select'
                  />
                )}
              />
            </>
          )}

          {hasSpecialField && (
            <FormTextField
              label={fieldName}
              required
              tooltip={specialFieldTooltip}
              defaultValue={getValues('oc_number')}
              {...register('oc_number')}
              errorMessage={errors.oc_number?.message}
            />
          )}
        </FormSection>

        <FormSection title='Meldeadresse'>
          <FormRow columns='grid-cols-[1fr_104px]'>
            <FormTextField
              label='Straße und Hausnummer'
              required
              autoComplete='address-line1'
              defaultValue={getValues('street_line')}
              {...register('street_line')}
              errorMessage={errors?.street_line?.message}
            />
            <FormTextField
              label='Hausnummer'
              required
              hideLabel
              placeholder='Nr'
              defaultValue={getValues('house_number')}
              {...register('house_number')}
              errorMessage={errors?.house_number?.message}
            />
          </FormRow>

          <FormTextField
            label='Adresszusatz'
            autoComplete='address-line2'
            defaultValue={getValues('additional_lines')}
            {...register('additional_lines')}
            errorMessage={errors?.additional_lines?.message}
          />

          <FormRow columns='grid-cols-[134px_1fr]'>
            <FormTextField
              label='Postleitzahl und Ort'
              required
              inputMode='numeric'
              maxLength={5}
              autoComplete='postal-code'
              defaultValue={getValues('postal_code')}
              {...register('postal_code')}
              errorMessage={errors.postal_code?.message}
            />
            <FormTextField
              label='Ort'
              required
              hideLabel
              autoComplete='address-level2'
              defaultValue={getValues('locality')}
              {...register('locality')}
              errorMessage={errors.locality?.message}
            />
          </FormRow>

          <Controller
            name='country_code'
            control={control}
            render={({ field, fieldState }) => (
              <FormSelectField
                label='Land'
                required
                options={countryOptions}
                searchable
                {...field}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </FormSection>

        {/* Figma: question + stacked radio cards below the address card */}
        {singer && (
          <Controller
            name='type'
            control={control}
            render={({ field, fieldState }) => (
              <FormRadioCardField
                name={field.name}
                label='Lebst Du mit dem Antragsteller in einer Lebensgemeinschaft?'
                required
                options={partnershipOptions}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
                onChange={(value) => field.onChange(value === 'true')}
                onBlur={field.onBlur}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        )}
      </div>

      {tabButton ? (
        <>
          {device === EDevice.MOBILE ? (
            <ButtonGroup
              className='back-next'
              type='back-next'
              data-testid='disabled-forward-button'
              buttonOneProps={{
                type: 'button',
                name: 'form-submit',
                dataTestId: 'next',
                children: 'nächster Schritt',
                loading,
                onClick: handleSubmit(submitData),
              }}
            />
          ) : (
            <div className='add-additional-person'>
              <Button
                variant='secondary-outlined'
                size='m'
                loading={loading}
                onClick={handleSubmit(submitData)}
              >
                nächster Schritt
              </Button>
            </div>
          )}
        </>
      ) : (
        <ButtonGroup
          type='back-next'
          className='back-next'
          buttonOneProps={{
            type: 'submit',
            name: 'form-submit',
            loading,
            dataTestId: 'start-fetching',
            children: 'nächster Schritt',
          }}
        />
      )}
    </form>
  );
};

export default PersonalTemplate;
